import { useCallback } from "react";
import type { CancelOptions, QueryFilters } from "@tanstack/react-query";
import { QueryFunction, QueryKey, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";

/**
 * Тип для функции-обновления данных в кэше, аналогичной обновлению в `useState`.
 * @template TData тип данных запроса
 */
export type QueryDataSetterUpdater<TData> = TData | ((prevData: TData | undefined) => TData | undefined) | undefined;

/** Опции фильтрации для {@link UseQueryDataResult.cancelQueries} без поля `queryKey`. */
export type UseQueryDataCancelQueryFilters = Omit<QueryFilters, "queryKey">;

/**
 * Результат хука {@link useQueryData}, расширяющий `UseQueryResult` методами работы с кэшем.
 * @template TData тип данных запроса
 * @template TError тип ошибки запроса
 */
export type UseQueryDataResult<TData, TError = unknown> = UseQueryResult<TData, TError> & {
    /** Обновляет или удаляет данные в кэше для текущего `queryKey`. */
    setData: (updater: QueryDataSetterUpdater<TData>) => void;
    /** Запись в кэш по произвольному `queryKey`. */
    setDataForKey: <T = unknown>(queryKey: QueryKey, updater: QueryDataSetterUpdater<T>) => void;
    /** Снимок кэша для текущего `queryKey` (не реактивный). */
    getData: () => TData | undefined;
    /** Чтение кэша по произвольному ключу. */
    getDataForKey: <T = unknown>(queryKey: QueryKey) => T | undefined;
    /** Отмена запросов для текущего `queryKey`. */
    cancelQueries: (filters?: UseQueryDataCancelQueryFilters, cancelOptions?: CancelOptions) => Promise<void>;
};

/**
 * Хук-обёртка над `useQuery` с `setData`, `getData`, `cancelQueries` без отдельного `useQueryClient`.
 *
 * Глобальные ошибки query/mutation обрабатываются в {@link QueryProvider} через `reportError`.
 *
 * @template TQueryFnData тип, возвращаемый `queryFn`
 * @template TError тип ошибки
 * @template TData окончательный тип данных (после `select`, если есть)
 */
export function useQueryData<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
    options: {
        queryKey: QueryKey;
        queryFn: QueryFunction<TQueryFnData, QueryKey>;
    } & Omit<UseQueryOptions<TQueryFnData, TError, TData>, "queryKey" | "queryFn" | "throwOnError">,
): UseQueryDataResult<TData, TError> {
    const queryClient = useQueryClient();

    const queryResult = useQuery({
        ...options,
        throwOnError: false,
        queryKey: options.queryKey,
        queryFn: options.queryFn,
    });

    const setData = useCallback(
        (updater: QueryDataSetterUpdater<TData>) => {
            queryClient.setQueryData<TData>(options.queryKey, updater);
        },
        [queryClient, options.queryKey],
    );

    const setDataForKey = useCallback(
        <T>(queryKeyItem: QueryKey, updater: QueryDataSetterUpdater<T>) => {
            queryClient.setQueryData<T>(queryKeyItem, updater);
        },
        [queryClient],
    );

    const getData = useCallback((): TData | undefined => {
        return queryClient.getQueryData<TData>(options.queryKey);
    }, [queryClient, options.queryKey]);

    const getDataForKey = useCallback(
        <T>(queryKeyItem: QueryKey): T | undefined => {
            return queryClient.getQueryData<T>(queryKeyItem);
        },
        [queryClient],
    );

    const cancelQueries = useCallback(
        (filters?: UseQueryDataCancelQueryFilters, cancelOptions?: CancelOptions) => {
            return queryClient.cancelQueries({ ...filters, queryKey: options.queryKey }, cancelOptions);
        },
        [queryClient, options.queryKey],
    );

    return {
        ...queryResult,
        setData,
        setDataForKey,
        getData,
        getDataForKey,
        cancelQueries,
    };
}
